import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import { useLocation } from 'react-router-dom';
import { NavigationNodeId } from '@pwc/pwc-deals-web-common';
import { MenuItem, Icon } from '@pwc/pwc-deals-ui';
import { getProject } from 'features/project/projectSlice';
import {
  analysesListPath,
  contentHubPath,
  filesPath,
  hypothesesListPath,
  legacyFilesPath,
  reportsListPath,
} from 'common/services/urlService';
import Accordion from 'common/components/Accordion';
import featureFlags from 'common/helpers/featureFlags';
import {
  analysesPreferredGroupListPath,
  byKfaPath,
  customPath,
} from 'features/analysesGroups/components/AnalysesGroupsContentRouter/AnalysesGroupsContentRouter';
import userTokenService from 'common/services/security/userTokenService';
import {
  GROUP_SETTING_KEY,
  getUserPreferences,
} from 'common/services/userPreferencesProjectService';

import './ControlledSideNav.style.scss';
import { getIsThirdPartyMember } from 'common/redux/security/loggedInUser/loggedInUserSlice';
import { getUserSettingsSelector } from 'common/redux/userSettings/userSettingsSlice';

type Props = {
  onClick?: (id: NavigationNodeId, url: string) => void;
  showHoverLabel?: boolean;
};

const NAVIGATION_I19_KEY_MAPPING: { [x: string]: string } = {
  Analyses: 'nav.analyses',
  Reports: 'nav.reports',
  Files: 'nav.files',
  'Content Hub': 'nav.contentHub',
  'Key Focus Area': 'nav.keyFocusArea',
};

export function ControlledSideNav({ onClick, showHoverLabel }: Props) {
  const { t } = useTranslation();
  const isThirdPartyMember = useSelector(getIsThirdPartyMember);
  const isInternalUser = userTokenService.isUserInternal();
  const location = useLocation();
  const [defaultAnalysisGroupBy, setDefaultAnalysisGroupBy] =
    useState('custom');
  const { id: projectId, navigation = [] } = useSelector(getProject);
  const visibleNavigation = useMemo(() => {
    return navigation
      .filter((nav) => !nav.isHidden)
      .filter((nav) => {
        if (nav.id === NavigationNodeId.CONTENT_HUB) {
          if (!featureFlags.isContentHubEnabled || isThirdPartyMember)
            return false;
          if (!featureFlags.isContentHubEnabledForClients && !isInternalUser)
            return false;
        }

        return true;
      });
  }, [navigation, isInternalUser, isThirdPartyMember]);
  const hiddenNavigation = useMemo(() => {
    const visibleIds = visibleNavigation.map((vn) => vn.id);

    return navigation.filter((nav) => !visibleIds.includes(nav.id));
  }, [visibleNavigation, navigation]);

  async function loadAnalysisGroupByPreferences() {
    try {
      const groupByPreferences = (await getUserPreferences(
        projectId,
        GROUP_SETTING_KEY
      )) as Record<string, string>[];
      const groupBySettingValue = groupByPreferences?.[0]?.settingValue;
      if (!groupBySettingValue) return;

      setDefaultAnalysisGroupBy(groupBySettingValue);
    } catch (error) {}
  }

  function getViewUrl(id: NavigationNodeId): string {
    switch (id) {
      case NavigationNodeId.KEY_FOCUS_AREAS:
        return hypothesesListPath(projectId);
      case NavigationNodeId.ANALYSES:
        return featureFlags.analysisGroupsPageEnabled
          ? analysesPreferredGroupListPath(projectId, defaultAnalysisGroupBy)
          : analysesListPath(projectId);
      case NavigationNodeId.REPORTS:
        return reportsListPath(projectId);
      case NavigationNodeId.FILES:
        return featureFlags.isEnabledFileFoldersFeature
          ? filesPath(projectId)
          : legacyFilesPath(projectId);
      case NavigationNodeId.CONTENT_HUB:
        return contentHubPath(projectId);
      default:
        return '/';
    }
  }

  function urlMatchesItemUrl(id: NavigationNodeId) {
    const url = getViewUrl(id);

    switch (id) {
      case NavigationNodeId.ANALYSES:
        return location.pathname.includes(
          url
            .replace(`/${customPath}`, '')
            .replace(`/${byKfaPath}`, '')
            .replace('-groups', '')
        );
      default:
        return location.pathname.includes(url);
    }
  }

  function getIcon(id: NavigationNodeId): string {
    switch (id) {
      case NavigationNodeId.KEY_FOCUS_AREAS:
        return 'hypothesis';
      case NavigationNodeId.ANALYSES:
        return 'analysis';
      case NavigationNodeId.REPORTS:
        return 'report';
      case NavigationNodeId.FILES:
        return 'files-feature';
      case NavigationNodeId.CONTENT_HUB:
        return 'books';
      default:
        return '';
    }
  }

  const userSettings = useSelector(getUserSettingsSelector);
  const { analysisFilterOption } = userSettings;

  useEffect(() => {
    void loadAnalysisGroupByPreferences();
  }, [projectId, analysisFilterOption]); // eslint-disable-line react-hooks/exhaustive-deps

  const getNavTitleI18ned = useCallback(
    (title: string) => {
      return NAVIGATION_I19_KEY_MAPPING[title]
        ? String(t(NAVIGATION_I19_KEY_MAPPING[title]))
        : title;
    },
    [t]
  );
  return (
    <>
      {visibleNavigation.map((nav) => (
        <MenuItem
          key={nav.id}
          icon={
            <Icon
              name={getIcon(nav.id)}
              className="outline icon-grey"
              width="24"
              height="24"
              {...(nav.id === NavigationNodeId.CONTENT_HUB
                ? { viewBox: '0 0 24 24' }
                : {})}
            />
          }
          title={getNavTitleI18ned(nav.title || '')}
          navTarget={getViewUrl(nav.id)}
          onClick={() => onClick?.(nav.id, getViewUrl(nav.id))}
          linkClass={cx({
            'menu-active': urlMatchesItemUrl(nav.id),
          })}
          showHoverLabel={!!showHoverLabel}
        />
      ))}
      {isInternalUser && hiddenNavigation.length > 0 && (
        <Accordion
          title={t('hiddenFromClientView')}
          className="hidden-side-nav-buttons"
          icon={<Icon name="folder-cross" width="24" viewBox="0 0 24 24" />}
          openIcon={<Icon name="up-chevron" width="12" />}
          closedIcon={<Icon name="down-chevron" width="12" />}
        >
          {hiddenNavigation.map((nav) => (
            <MenuItem
              key={nav.id}
              icon={
                <Icon
                  name={getIcon(nav.id)}
                  className="outline icon-grey"
                  width="24"
                  height="24"
                  {...(nav.id === NavigationNodeId.CONTENT_HUB
                    ? { viewBox: '0 0 24 24' }
                    : {})}
                />
              }
              title={getNavTitleI18ned(nav.title || '')}
              navTarget={getViewUrl(nav.id)}
              onClick={() => onClick?.(nav.id, getViewUrl(nav.id))}
              linkClass={cx({
                'menu-active': urlMatchesItemUrl(nav.id),
              })}
              showHoverLabel={!!showHoverLabel}
            />
          ))}
        </Accordion>
      )}
    </>
  );
}

export default ControlledSideNav;
